import { Component, type ErrorInfo, type PropsWithChildren } from 'react';

import { isAxiosError } from 'axios';
import { type TFunction } from 'i18next';

import { logErrorMini } from '@/shared/services/service-logger/lib/log-error-mini';
import { ErrorPage } from '@/shared/ui/error-page/ErrorPage';

interface Props extends PropsWithChildren {
  t: TFunction;
}

interface State {
  hasError: boolean;
  errorType?: 'axios' | 'cssLoad' | 'unknown';
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = {
    hasError: false,
  };

  static getDerivedStateFromError(): State {
    return { hasError: true, errorType: 'unknown' };
  }

  checkStyles = (): State => {
    try {
      let hasRootStyles = false;

      for (const sheet of Array.from(document.styleSheets)) {
        try {
          for (const rule of Array.from(sheet.cssRules)) {
            if (rule instanceof CSSStyleRule && rule.selectorText === '#root') {
              hasRootStyles = true;
              break;
            }
          }
        } catch (e) {
          continue;
        }

        if (hasRootStyles) break;
      }

      if (!hasRootStyles) {
        throw new Error('Root element styles not found');
      }

      return {
        hasError: false,
      };
    } catch (error) {
      return {
        hasError: true,
        errorType: 'cssLoad',
      };
    }
  };

  constructor(props: Props) {
    super(props);
    this.state = { ...this.checkStyles() };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({
      hasError: true,
      errorType: isAxiosError(error) ? 'axios' : 'unknown',
    });

    void logErrorMini(error, info.componentStack ?? error.message);
  }

  isReloadableError = () => {
    return this.state.errorType === 'axios' || this.state.errorType === 'cssLoad';
  };

  render() {
    const { t } = this.props;
    if (this.state.hasError) {
      return (
        <ErrorPage
          onClickButton={
            this.isReloadableError()
              ? () => window.location.reload()
              : () => (window.location.pathname = '/')
          }
          buttonLabel={this.isReloadableError() ? t('Reload Page') : t('Go to Games')}
        />
      );
    }

    return this.props.children;
  }
}
